import React, { useMemo, useState } from 'react';
import { FilledButton, OutlinedButton, Text } from '@make-software/tg-mini-app-ui';

import {
  BaseModal,
  copyToClipboard,
  openInExternalBrowser,
  CloseIconButton,
  ModalContentWrapper,
  generateOtp,
  getIosBrowserKind,
  getPlatform
} from '@/shared';
import CloseIcon from '@/shared/assets/icons/ic-close.svg';

import { OtpSection, type OTPState } from './install-to-home-screen.otp-section';
import { StepsSection } from './install-to-home-screen.steps-section';
import {
  ModalRoot,
  HeaderRow,
  Title,
  ContentArea,
  StickyFooter,
  Section,
  Hint,
  FooterRow
} from './install-to-home-screen.styled';

const OTP_TTL_MS = 60 * 60 * 1000;

interface InstallToHomeScreenModalProps {
  openModal: boolean;
  handleCloseModal: () => void;
  externalUrl: string;
  onOpenInBrowser?: () => void;
  disabled?: boolean;
}

export const InstallToHomeScreenModal = ({
  openModal,
  handleCloseModal,
  externalUrl,
  onOpenInBrowser,
  disabled
}: InstallToHomeScreenModalProps) => {
  const platform = useMemo(() => getPlatform(), []);
  const isMobile = platform === 'ios' || platform === 'android';

  const iosBrowserKind = useMemo(
    () => (platform === 'ios' ? getIosBrowserKind() : 'other'),
    [platform]
  );

  const isIosSafari = platform === 'ios' && iosBrowserKind === 'safari';
  const showOpenInBrowserStep = !(platform === 'ios' && isIosSafari);

  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const [otpState, setOtpState] = useState<OTPState>({ status: 'idle' });
  const [otpCopied, setOtpCopied] = useState(false);

  const openExternal = () => {
    if (onOpenInBrowser) return onOpenInBrowser();
    openInExternalBrowser(externalUrl);
  };

  const handleCopyLink = async () => {
    setCopyFailed(false);
    const ok = await copyToClipboard(externalUrl);
    if (!ok) {
      setCopyFailed(true);
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const requestOtp = async () => {
    if (!isMobile || disabled) return;

    setOtpState({ status: 'loading' });

    const res = await generateOtp();

    if (res.error || (res.httpCode && res.httpCode >= 400)) {
      setOtpState({
        status: 'error',
        message: res.error?.message || 'Failed to generate code. Try again.'
      });
      return;
    }

    const otp = res.data?.otp;
    if (!otp) {
      setOtpState({
        status: 'error',
        message: 'Failed to generate code. Try again.'
      });
      return;
    }

    const expiresAtMs = res.data?.expiresAtMs ?? Date.now() + OTP_TTL_MS;

    setOtpState({
      status: 'ready',
      code: otp,
      expiresAtMs
    });
  };

  const copyOtp = async () => {
    if (otpState.status !== 'ready') return;
    const ok = await copyToClipboard(otpState.code);
    if (!ok) return;
    setOtpCopied(true);
    window.setTimeout(() => setOtpCopied(false), 1200);
  };

  return (
    <BaseModal
      onRequestClose={handleCloseModal}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      isOpen={openModal}
      variant="centered"
      onAfterOpen={requestOtp}
    >
      <ModalContentWrapper direction={'column'} style={{ height: '100%', width: '100%' }}>
        <ModalRoot>
          <HeaderRow>
            <Title>Add TEA to Home Screen</Title>

            <CloseIconButton
              iconSrc={CloseIcon}
              onClick={handleCloseModal}
              size={35}
              borderRadius="50%"
              iconColor="white"
              aria-label="Close modal"
            />
          </HeaderRow>

          <ContentArea>
            {!isMobile ? (
              <Section>
                <Text weight={1} size="sm" color="white">
                  Mobile only
                </Text>
                <Hint>
                  This task can only be completed on iOS or Android. Open TEA on your phone to
                  continue.
                </Hint>
              </Section>
            ) : (
              <>
                <OtpSection
                  otpState={otpState}
                  disabled={disabled}
                  otpCopied={otpCopied}
                  onRequestOtp={requestOtp}
                  onCopyOtp={() => void copyOtp()}
                />

                <StepsSection platform={platform} showOpenInBrowserStep={showOpenInBrowserStep} />
              </>
            )}
          </ContentArea>

          <StickyFooter>
            <FooterRow>
              <Text weight={1} size="sm" color="white">
                {copied ? 'Copied ✓' : copyFailed ? 'Copy failed' : 'Need the link?'}
              </Text>

              <OutlinedButton
                onClick={handleCopyLink}
                title={copied ? 'Copied' : 'Copy link'}
                height={28}
                disabled={disabled}
              />
            </FooterRow>

            <FilledButton
              title="Open in browser"
              onClick={() => {
                if (otpState.status === 'idle') void requestOtp();
                openExternal();
              }}
              disabled={disabled || !isMobile}
              borderRadius="0"
            />

            {copyFailed && (
              <Hint>
                Copy is blocked by the browser. Try selecting the URL manually or open the link from
                a normal browser.
              </Hint>
            )}
          </StickyFooter>
        </ModalRoot>
      </ModalContentWrapper>
    </BaseModal>
  );
};
